import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { useAuthStore } from '@/store/authStore';

// Onboarding step definition
export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  target?: string; // CSS selector for highlight
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  spotlightPadding?: number;
  action?: {
    type: 'click' | 'navigate' | 'wait';
    target?: string;
    waitMs?: number;
  };
  showSkip?: boolean;
  showBack?: boolean;
  nextLabel?: string;
}

// Onboarding context
interface OnboardingContextType {
  isActive: boolean;
  currentStep: number;
  steps: OnboardingStep[];
  currentStepData: OnboardingStep | null;
  startOnboarding: () => void;
  endOnboarding: (completed?: boolean) => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (index: number) => void;
  skipOnboarding: () => void;
  hasCompletedOnboarding: boolean;
  resetOnboarding: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

// Onboarding steps definition
const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Üdvözlünk a Tremoriában!',
    description: 'Örülünk, hogy itt vagy! Készítettünk egy rövid bemutatót, hogy megismerd az alkalmazás funkcióit.',
    position: 'center',
    showSkip: true,
    nextLabel: 'Kezdjük!',
  },
  {
    id: 'dashboard',
    title: 'Irányítópult',
    description: 'Ez az irányítópultod, ahol egy helyen láthatod az összes fontos információt: ékszereid, statisztikák és értesítések.',
    target: '[data-tour="dashboard-stats"]',
    position: 'bottom',
    showSkip: true,
    showBack: true,
  },
  {
    id: 'jewelry-list',
    title: 'Ékszereim',
    description: 'Itt találod az összes aktivált ékszered. Kattints bármelyikre a részletek megtekintéséhez és tartalmak kezeléséhez.',
    target: '[data-tour="jewelry-list"]',
    position: 'bottom',
    showSkip: true,
    showBack: true,
  },
  {
    id: 'activate-button',
    title: 'Ékszer aktiválása',
    description: 'Új ékszert az "Aktiválás" gombbal adhatsz hozzá. Olvasd be az NFC chipet vagy írd be a kódot.',
    target: '[data-tour="activate-button"]',
    position: 'left',
    showSkip: true,
    showBack: true,
  },
  {
    id: 'sidebar-nav',
    title: 'Navigáció',
    description: 'Az oldalsávból érheted el az összes funkciót: ékszerek, beállítások, és értesítések.',
    target: '[data-tour="sidebar-nav"]',
    position: 'right',
    showSkip: true,
    showBack: true,
  },
  {
    id: 'theme-toggle',
    title: 'Sötét mód',
    description: 'A jobb felső sarokban váltogathatsz a világos és sötét téma között.',
    target: '[data-tour="theme-toggle"]',
    position: 'bottom',
    showSkip: true,
    showBack: true,
  },
  {
    id: 'settings',
    title: 'Beállítások',
    description: 'A Beállítások menüben kezelheted a fiókodat, biztonsági opciókat és értesítéseket.',
    target: '[data-tour="settings-link"]',
    position: 'right',
    showSkip: true,
    showBack: true,
  },
  {
    id: 'complete',
    title: 'Kész!',
    description: 'Most már készen állsz a Tremoria használatára! Ha bármikor segítségre van szükséged, a súgóban megtalálsz minket.',
    position: 'center',
    showBack: true,
    nextLabel: 'Befejezés',
  },
];

const STORAGE_KEY = 'tremoria_onboarding_completed';
const STORAGE_KEY_SKIPPED = 'tremoria_onboarding_skipped';

interface OnboardingProviderProps {
  children: ReactNode;
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const { user, isAuthenticated } = useAuthStore();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  // Initialize from localStorage directly (lazy initialization)
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(() => {
    if (typeof window === 'undefined') return true;
    const completed = localStorage.getItem(STORAGE_KEY);
    const skipped = localStorage.getItem(STORAGE_KEY_SKIPPED);
    return completed === 'true' || skipped === 'true';
  });

  // Auto-start onboarding for new users
  useEffect(() => {
    if (isAuthenticated && user && !hasCompletedOnboarding && !isActive) {
      // Check if user is new (created within last hour)
      const userCreatedAt = user.createdAt ? new Date(user.createdAt) : null;
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);

      if (userCreatedAt && userCreatedAt > oneHourAgo) {
        // Small delay to let the dashboard render
        const timer = setTimeout(() => {
          setIsActive(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [isAuthenticated, user, hasCompletedOnboarding, isActive]);

  const startOnboarding = useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  const endOnboarding = useCallback((completed = true) => {
    setIsActive(false);
    setCurrentStep(0);
    if (completed) {
      localStorage.setItem(STORAGE_KEY, 'true');
      setHasCompletedOnboarding(true);
    }
  }, []);

  const skipOnboarding = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    localStorage.setItem(STORAGE_KEY_SKIPPED, 'true');
    setHasCompletedOnboarding(true);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      endOnboarding(true);
    }
  }, [currentStep, endOnboarding]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((index: number) => {
    if (index >= 0 && index < ONBOARDING_STEPS.length) {
      setCurrentStep(index);
    }
  }, []);

  const resetOnboarding = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY_SKIPPED);
    setHasCompletedOnboarding(false);
    setCurrentStep(0);
  }, []);

  const currentStepData = isActive ? ONBOARDING_STEPS[currentStep] : null;

  return (
    <OnboardingContext.Provider
      value={{
        isActive,
        currentStep,
        steps: ONBOARDING_STEPS,
        currentStepData,
        startOnboarding,
        endOnboarding,
        nextStep,
        prevStep,
        goToStep,
        skipOnboarding,
        hasCompletedOnboarding,
        resetOnboarding,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}
